/**
 * Capture / move overlay animation.
 * Renders a single piece flying along the move path (over the board) for `duration` ms.
 * Used to give visual feedback on captures (multi-jump arc) before the static board updates.
 *
 * Props:
 *  - path: [[x,y], [x,y], ...] in board coords
 *  - flipped: boolean (board orientation)
 *  - owner: 'A' | 'B'
 *  - king: boolean
 *  - duration: total ms (path is split per segment)
 *  - onDone: callback when animation completes
 */
import { useEffect, useRef, useState } from "react";
import Piece from "./Piece";
import { usePreferences } from "../context/PreferencesContext";

const SIZE = 5;

function coordToPercent(x, y, flipped) {
  // x: column 0..SIZE-1, y: row 0..SIZE-1 (y=0 is bottom)
  // Cell center = (col + 0.5) * (100/SIZE), (row + 0.5) * (100/SIZE)
  const col = flipped ? SIZE - 1 - x : x;
  const rowFromTop = flipped ? y : SIZE - 1 - y;
  return {
    leftPct: ((col + 0.5) * 100) / SIZE,
    topPct: ((rowFromTop + 0.5) * 100) / SIZE,
  };
}

export default function MoveOverlay({ path, flipped, owner, king, themeId: themeIdProp, duration = 600, onDone }) {
  const { pieceTheme: prefTheme } = usePreferences();
  const themeId = themeIdProp || prefTheme;
  const [segmentIndex, setSegmentIndex] = useState(0);
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!path || path.length < 2) {
      onDone?.();
      return;
    }
    setSegmentIndex(0);
    const segments = path.length - 1;
    const perSegment = duration / segments;
    let cancelled = false;

    function step() {
      if (cancelled) return;
      if (startTimeRef.current == null) startTimeRef.current = performance.now();
      const elapsed = performance.now() - startTimeRef.current;
      const idx = Math.min(segments - 1, Math.floor(elapsed / perSegment));
      setSegmentIndex(idx);
      if (elapsed < duration) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        onDone?.();
      }
    }
    rafRef.current = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startTimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  if (!path || path.length < 2) return null;

  const segments = path.length - 1;
  const perSegment = duration / segments;
  const elapsed = startTimeRef.current ? performance.now() - startTimeRef.current : 0;
  const localT = Math.min(1, (elapsed - segmentIndex * perSegment) / perSegment);

  const from = path[segmentIndex];
  const to = path[segmentIndex + 1];
  const a = coordToPercent(from[0], from[1], flipped);
  const b = coordToPercent(to[0], to[1], flipped);
  // Linear interpolation
  const left = a.leftPct + (b.leftPct - a.leftPct) * localT;
  const top = a.topPct + (b.topPct - a.topPct) * localT;
  // Arc lift (parabola): -lift * 4 * t * (1 - t) gives a soft bounce above the line
  const isJump = segments > 0; // anything moves with a slight lift
  const lift = isJump ? 4 * localT * (1 - localT) * 2.2 : 0; // in % of board height

  // SVG trail for the full path (golden glow)
  const trailPoints = path.map(([x, y]) => coordToPercent(x, y, flipped));

  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="move-overlay">
      {/* Path trail */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <polyline
          points={trailPoints.map((p) => `${p.leftPct},${p.topPct}`).join(" ")}
          fill="none"
          stroke="#f5a623"
          strokeOpacity="0.55"
          strokeWidth="0.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 6px rgba(245,166,35,0.7))" }}
        />
      </svg>

      {/* Flying piece */}
      <div
        className="absolute"
        style={{
          left: `${left}%`,
          top: `calc(${top}% - ${lift}%)`,
          width: `calc(${100 / SIZE}% * 0.58)`,
          height: `calc(${100 / SIZE}% * 0.58)`,
          transform: "translate(-50%, -50%)",
          willChange: "left, top",
        }}
      >
        <Piece
          owner={owner}
          king={king}
          themeId={themeId}
          size="100%"
          showHints={false}
          className="!relative !left-0 !top-0 !translate-x-0 !translate-y-0 shadow-[0_8px_20px_rgba(0,0,0,0.6),0_0_18px_rgba(245,166,35,0.45)]"
        />
      </div>
    </div>
  );
}
